import { thirdPartyWidgets } from '../../util/thirdPartyServices';

/**
 * Boots Intercom with the signed-in user and tells ProfitWell who they are (App#trackBilling, after
 * every update). Neither happens on the pages of the Medsender iOS app's sign-in
 * (isIosAppSignInFlowPage), nor while impersonating; and a widget whose script is not loaded is
 * skipped rather than called.
 *
 * @param {object} props App's user, isAdmin, intercomHmac, currentOrganizationId, impersonating
 * @param {boolean} isBillingTracked whether ProfitWell already has the user
 * @param {{ location?: Location, win?: Window }} env
 * @returns {boolean} the new isBillingTracked
 */
const trackThirdPartyServices = (
  {
    user, isAdmin, intercomHmac, currentOrganizationId, impersonating,
  },
  isBillingTracked,
  { location = window.location, win = window } = {},
) => {
  if (impersonating) return isBillingTracked;
  const { intercom, profitwell } = thirdPartyWidgets(location, win);
  const isAuthenticated = user !== null;

  if (isAuthenticated && !isBillingTracked) {
    const customerPaymentId = localStorage.getItem('customer_payment_id');

    let email = localStorage.getItem('email') || localStorage.getItem('m8_uid') || user.uid;
    if (email === 'undefined') {
      // Sometimes local storage has the value undefined. if so let's use the user uid attribute.
      email = user.uid;
    }

    let name = localStorage.getItem('m8_name') || user.fullName;
    if (name === 'undefined') {
      // Sometimes local storage has the value undefined. if so let's use the user uid attribute.
      name = user.fullName;
    }
    const customerPaymentIdExists = customerPaymentId && customerPaymentId !== 'null' && customerPaymentId !== 'undefined';

    if (name && intercomHmac && intercom) {
      const userFlags = JSON.parse(localStorage.getItem('flags'));

      let { autoUploadEmr } = user;
      if (autoUploadEmr === 'null') {
        autoUploadEmr = '';
      }

      const userData = {
        api_base: 'https://api-iam.intercom.io',
        app_id: process.env.INTERCOM_APP_ID,
        name: user.fullName,
        email,
        user_hash: intercomHmac,
        organization: user.organization,
        plan_type: localStorage.getItem('m8_plan_type'),
        emr: autoUploadEmr || user.emr,
        validEMRCredentials: !!autoUploadEmr,
        isAdmin,
      };
      if (currentOrganizationId && user.organization) {
        // Associate the user with an Intercom Company (auto-created on first boot).
        // Keyed by org ID so renaming an org updates the company instead of forking it.
        userData.company = {
          company_id: String(currentOrganizationId),
          name: user.organization,
        };
        if (customerPaymentIdExists) {
          // Custom company attribute linking to the org's Chargebee customer page.
          userData.company.chargebee_customer_url = `https://medsender.chargebee.com/d/customers/${customerPaymentId}`;
        }
      }
      if (userFlags && userFlags.data) {
        userFlags.data.flags.forEach((flag) => {
          if (flag.name === 'referral_mgmt_trial' && flag.active && flag.expiration) {
            // If the flag is referral mgmt trial and there is an expiry, include
            // if trial is expired or not.
            const expiration = Date.parse(flag.expiration);
            userData.referralMgmtTrialExpired = expiration < new Date();
          }
          userData[flag.name] = flag.active;
        });
      }
      intercom('boot', userData);
    }

    if ((customerPaymentIdExists || email) && profitwell) {
      profitwell('user_id', customerPaymentIdExists ? customerPaymentId : email);
      return true;
    }
  }
  if (!isAuthenticated && isBillingTracked && profitwell) {
    profitwell('user_id', null);
    return false;
  }
  return isBillingTracked;
};

/**
 * Ends the Intercom session on sign-out, when Intercom is loaded on this page.
 *
 * @param {Window} win
 */
export const shutdownIntercom = (win = window) => {
  if (typeof win.Intercom === 'function') win.Intercom('shutdown');
};

export default trackThirdPartyServices;
